/**
 * UploadTile — `.upload-tile` from the prototype (cms-redesign-mockup.html
 * ~4681–4710, used in the Sharing & Media section ~8330–8355).
 *
 *   <UploadTile
 *     label="Session Image"
 *     required
 *     hint="In‑app cover"
 *     value={formData.session_image_preview || formData.session_image}
 *     uploading={!!formData.session_image_uploading}
 *     accept="image/*"
 *     onChange={(file) => handleImageUpload({ target: { files: [file] } })}
 *     onRemove={() => setFormData(prev => ({ ...prev, session_image: '', session_image_preview: '' }))}
 *   />
 *
 * Visual recipe:
 *   • Cream bg, dashed line-strong border, radius-md, padding 14
 *   • Hover → solid teal-green border + surface bg
 *   • Preview: 4:3 aspect, rounded-sm, cover image OR empty-state
 *     glyph with "Click to upload" caption
 *   • Filename: 12/600 navy (derived from URL last segment)
 *   • Specs: 11 muted (file extension + "hosted"/"new")
 *   • Acts: Replace (default btn) + Remove (ghost, coral)
 *
 * Click anywhere on the empty tile → triggers the file picker. When
 * populated, only the Replace button does — keeps accidental swaps
 * unlikely while the file is "settled".
 */
import React, { useRef } from 'react';
import { cn } from '../cn';

/* Derive a clean filename from a URL. Used to show something
 * recognizable in the populated state — for new uploads (object
 * URLs starting with `blob:`) we fall back to a "(pending upload)"
 * label since blob URLs are opaque. */
function deriveFilename(url) {
  if (!url) return '';
  if (url.startsWith('blob:') || url.startsWith('data:')) return '(pending upload)';
  try {
    const u = new URL(url);
    const last = u.pathname.split('/').filter(Boolean).pop();
    return last ? decodeURIComponent(last) : url;
  } catch {
    // Not a parseable URL — best effort split
    const last = String(url).split('/').filter(Boolean).pop();
    return last || String(url);
  }
}

function deriveExtension(url) {
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) return null;
  const m = String(url).match(/\.([a-z0-9]{2,5})(?:\?|#|$)/i);
  return m ? m[1].toUpperCase() : null;
}

const UploadIconSvg = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

export default function UploadTile({
  label,
  required,
  hint,
  value,
  uploading,
  accept = 'image/*',
  onChange,
  onRemove,
  emptyText = 'Click to upload',
  className,
  inputId,
  // Optional `name` for the hidden file input (form semantics / tests).
  name,
  // Caps the tile width — pass a small value (e.g. 176) for icon-sized
  // uploads so the tile doesn't stretch to the full form width. Default:
  // unconstrained (full width), preserving the banner/cover call sites.
  maxWidth,
  // Aspect ratio of the preview box. Defaults to 4:3 (covers); pass '1 / 1'
  // for square icon previews.
  previewAspectRatio = '4 / 3',
}) {
  const inputRef = useRef(null);
  const hasValue = !!value;

  const openPicker = () => {
    if (inputRef.current) inputRef.current.click();
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Bubble the raw file up — parent already has handlers expecting
    // either an event-style object or a File. We pass through the
    // event so parents that read `e.target.files[0]` keep working.
    if (onChange) onChange(e, file);
    // Reset so picking the same file again still fires.
    e.target.value = '';
  };

  const ext = deriveExtension(value);
  const filename = deriveFilename(value);

  return (
    <div className={cn('flex flex-col', className)} style={{ minWidth: 0, maxWidth }}>
      {label && (
        <label
          className="font-condensed font-bold uppercase text-text-light"
          htmlFor={inputId}
          style={{
            fontSize: 11,
            letterSpacing: '0.08em',
            marginBottom: 6,
            display: 'flex',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: 4,
          }}
        >
          <span>{label}</span>
          {required && (
            <span
              className="text-coral"
              style={{
                fontWeight: 400,
                textTransform: 'none',
                letterSpacing: 0,
                fontFamily: 'var(--font-sans)',
              }}
            >
              *
            </span>
          )}
          {hint && (
            <span
              className="text-text-muted"
              style={{
                fontWeight: 400,
                textTransform: 'none',
                letterSpacing: 0,
                fontFamily: 'var(--font-sans)',
                fontSize: 11,
              }}
            >
              — {hint}
            </span>
          )}
        </label>
      )}

      <div
        className="upload-tile"
        style={{
          background: 'rgb(var(--color-cream))', /* cream */
          border: hasValue
            ? '1px solid rgb(var(--color-sand))' /* sand */
            : '1px dashed #D6CCAE', /* line-strong */
          borderRadius: 8,
          padding: 14,
          transition: 'all 200ms ease',
          cursor: hasValue ? 'default' : 'pointer',
          minWidth: 0,
        }}
        onClick={hasValue ? undefined : openPicker}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'rgb(var(--color-teal-green))'; /* teal */
          e.currentTarget.style.borderStyle = 'solid';
          e.currentTarget.style.background = 'rgb(var(--color-surface))'; /* surface */
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = hasValue ? 'rgb(var(--color-sand))' : '#D6CCAE';
          e.currentTarget.style.borderStyle = hasValue ? 'solid' : 'dashed';
          e.currentTarget.style.background = 'rgb(var(--color-cream))';
        }}
      >
        {/* Preview area. Real <img> with `object-fit: contain` so the
            whole source image stays visible — quick-tap covers are
            often portrait and deep-link cards are wide 1200×630, so
            letting the image fit + letterbox keeps composition
            readable instead of cropping off important content. The
            cream-dark wrapper bg fills the leftover space. */}
        <div
          style={{
            width: '100%',
            aspectRatio: previewAspectRatio,
            borderRadius: 6,
            display: 'grid',
            placeItems: 'center',
            position: 'relative',
            overflow: 'hidden',
            marginBottom: 10,
            background: 'rgb(var(--color-cream-dark))', /* matte behind the image — theme-aware */
            boxShadow: hasValue ? 'inset 0 0 0 1px #00000010' : 'none',
            color: hasValue ? '#FFFFFF' : '#9CA1A8',
          }}
        >
          {hasValue && (
            <img
              src={value}
              alt=""
              aria-hidden
              draggable={false}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'center',
                display: 'block',
              }}
            />
          )}
          {!hasValue && (
            <div
              className="flex flex-col items-center"
              style={{ gap: 6, padding: 14, textAlign: 'center' }}
            >
              {UploadIconSvg}
              <span style={{ fontSize: 12 }}>{emptyText}</span>
            </div>
          )}
          {uploading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: '#FAF4EAE0',
                display: 'grid',
                placeItems: 'center',
                fontSize: 12,
                color: '#5C7E72', /* teal-d */
                fontWeight: 600,
              }}
            >
              Uploading…
            </div>
          )}
        </div>

        {/* Filename + specs (populated state) OR call-to-action
            placeholder (empty state). Keeping the height roughly
            consistent across both states so the grid doesn't jump
            when a tile flips between empty and populated. */}
        {hasValue ? (
          <>
            <div
              className="text-navy"
              style={{
                fontSize: 12,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={filename}
            >
              {filename}
            </div>
            <div
              className="text-text-muted"
              style={{ fontSize: 11, marginTop: 2 }}
            >
              {ext ? `${ext} · hosted` : 'hosted'}
            </div>
          </>
        ) : (
          <>
            <div
              className="text-text-muted"
              style={{ fontSize: 12, fontWeight: 500 }}
            >
              No image yet
            </div>
            <div
              className="text-text-muted"
              style={{ fontSize: 11, marginTop: 2 }}
            >
              {accept.startsWith('image') ? 'PNG / JPG / WebP' : accept}
            </div>
          </>
        )}

        {/* Action row — only shown when populated; the empty card is
            itself the call-to-action so we skip the buttons there. */}
        {hasValue && (
          <div
            className="flex"
            style={{ gap: 6, marginTop: 8 }}
            // Stop the wrapper's click-to-upload from also firing
            // when the user clicks inside this row.
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={openPicker}
              className="font-condensed font-bold uppercase cursor-pointer"
              style={{
                flex: 1,
                height: 28,
                padding: '0 10px',
                border: '1px solid rgb(var(--color-sand))',
                borderRadius: 4,
                background: 'rgb(var(--color-surface))',
                color: 'rgb(var(--color-navy))',
                fontSize: 11,
                letterSpacing: '0.06em',
              }}
            >
              Replace
            </button>
            {onRemove && (
              <button
                type="button"
                onClick={onRemove}
                className="font-condensed font-bold uppercase cursor-pointer"
                style={{
                  height: 28,
                  padding: '0 10px',
                  border: '1px solid transparent',
                  borderRadius: 4,
                  background: 'transparent',
                  color: '#C4715B', /* coral-d */
                  fontSize: 11,
                  letterSpacing: '0.06em',
                }}
              >
                Remove
              </button>
            )}
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        id={inputId}
        name={name}
        type="file"
        accept={accept}
        onChange={handleFile}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      />
    </div>
  );
}
